import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { logger } from '@/utils/logger';
import { InputSanitizer, RateLimiter } from '@/utils/security';

export const SignInForm = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Input validation
    const emailValidation = InputSanitizer.validateEmail(formData.email);
    if (!emailValidation.isValid) {
      toast({
        title: "Invalid Email",
        description: emailValidation.reason,
        variant: "destructive",
      });
      return;
    }

    // Rate limiting for password reset
    const rateLimitKey = `reset_${formData.email}`;
    const rateLimit = RateLimiter.checkLimit(rateLimitKey, 3, 60 * 60 * 1000); // 3 attempts per hour
    
    if (!rateLimit.allowed) {
      const resetTime = new Date(rateLimit.resetTime);
      toast({
        title: "Too Many Reset Requests",
        description: `Please try again at ${resetTime.toLocaleTimeString()}`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Sanitize email input
      const sanitizedEmail = InputSanitizer.sanitizeSql(formData.email.trim().toLowerCase());
      
      // Construct the redirect URL securely
      const baseUrl = window.location.protocol + '//' + window.location.host;
      const redirectUrl = `${baseUrl}/auth/callback?next=/reset-password`;
      
      logger.debug('Password reset requested', {
        component: 'SignInForm',
        action: 'resetPassword'
      }, { redirectUrl });

      const { error } = await supabase.auth.resetPasswordForEmail(sanitizedEmail, {
        redirectTo: redirectUrl,
      });

      if (error) {
        RateLimiter.recordAttempt(rateLimitKey);
        throw error;
      }

      logger.info('Password reset email sent', {
        component: 'SignInForm',
        action: 'resetPassword'
      }, { email: sanitizedEmail });

      toast({
        title: "Check your email",
        description: "We've sent you a password reset link",
      });
      setIsResetMode(false);
    } catch (error: any) {
      logger.error('Password reset failed', {
        component: 'SignInForm',
        action: 'resetPassword'
      }, error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Input validation
    const emailValidation = InputSanitizer.validateEmail(formData.email);
    if (!emailValidation.isValid) {
      toast({
        title: "Invalid Email",
        description: emailValidation.reason,
        variant: "destructive",
      });
      return;
    }

    // Rate limiting check
    const rateLimitKey = `auth_${formData.email}`;
    const rateLimit = RateLimiter.checkLimit(rateLimitKey, 5, 15 * 60 * 1000);
    
    if (!rateLimit.allowed) {
      const resetTime = new Date(rateLimit.resetTime);
      toast({
        title: "Too Many Attempts",
        description: `Please try again at ${resetTime.toLocaleTimeString()}`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Sanitize email input
      const sanitizedEmail = InputSanitizer.sanitizeSql(formData.email.trim().toLowerCase());
      
      const { error } = await supabase.auth.signInWithPassword({
        email: sanitizedEmail,
        password: formData.password,
      });

      if (error) {
        // Record failed attempt for rate limiting
        RateLimiter.recordAttempt(rateLimitKey);
        
        // Log security event
        logger.warn('Failed sign in attempt', {
          component: 'SignInForm',
          action: 'signIn'
        }, {
          email: sanitizedEmail,
          error: error.message,
          remaining: rateLimit.remaining - 1
        });
        
        throw error;
      }

      // Reset rate limiting on success
      RateLimiter.reset(rateLimitKey);

      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({ queryKey: ['session'] });

      logger.info('User signed in successfully', {
        component: 'SignInForm',
        action: 'signIn'
      }, {
        email: sanitizedEmail
      });

      toast({
        title: "Success",
        description: "You have been signed in",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {isResetMode ? "Reset Password" : "Welcome back"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isResetMode 
            ? "Enter your email to receive a reset link" 
            : "Enter your credentials to sign in"}
        </p>
      </div>
      
      <form onSubmit={isResetMode ? handleResetPassword : handleSignIn} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
            className="border-input/50 focus:border-input"
          />
        </div>

        {!isResetMode && (
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              className="border-input/50 focus:border-input"
            />
          </div>
        )}

        <Button 
          type="submit" 
          className="w-full"
          disabled={isLoading}
        >
          {isLoading 
            ? (isResetMode ? "Sending reset link..." : "Signing in...") 
            : (isResetMode ? "Send reset link" : "Sign in")}
        </Button>

        <Button
          type="button"
          variant="link"
          className="w-full text-sm text-muted-foreground hover:text-primary"
          onClick={() => setIsResetMode(!isResetMode)}
        >
          {isResetMode ? "Back to sign in" : "Forgot password?"}
        </Button>
      </form>
    </div>
  );
};